/**
 * Shared 2D polygon helpers for the Massing & Site tab.
 *
 * Used by the toposolid split/merge tools, building pads, graded regions and
 * the site renderers. All polygons are closed implicitly: arrays of [x, y]
 * pairs where the last vertex connects back to the first.
 */

export type Polygon2D = [number, number][];

/** Signed-area magnitude of a polygon (shoelace). */
export function polygonArea(poly: Polygon2D): number {
    let area = 0;
    const n = poly.length;
    for (let i = 0; i < n; i++) {
        const j = (i + 1) % n;
        area += poly[i][0] * poly[j][1];
        area -= poly[j][0] * poly[i][1];
    }
    return Math.abs(area) / 2;
}

/** Positive CCW orientation flag for a polygon. */
export function isCCW(poly: Polygon2D): boolean {
    let area = 0;
    const n = poly.length;
    for (let i = 0; i < n; i++) {
        const j = (i + 1) % n;
        area += poly[i][0] * poly[j][1];
        area -= poly[j][0] * poly[i][1];
    }
    return area >= 0;
}

/** Ray-casting point-in-polygon test. */
export function pointInPolygon(p: [number, number], poly: Polygon2D): boolean {
    let inside = false;
    const n = poly.length;
    for (let i = 0, j = n - 1; i < n; j = i++) {
        const [xi, yi] = poly[i];
        const [xj, yj] = poly[j];
        if (yi > p[1] !== yj > p[1] && p[0] < ((xj - xi) * (p[1] - yi)) / (yj - yi) + xi) {
            inside = !inside;
        }
    }
    return inside;
}

/** Centroid of a polygon's vertices (area-weighted). */
export function polygonCentroid(poly: Polygon2D): [number, number] {
    let cx = 0;
    let cy = 0;
    let a = 0;
    const n = poly.length;
    for (let i = 0; i < n; i++) {
        const j = (i + 1) % n;
        const cross = poly[i][0] * poly[j][1] - poly[j][0] * poly[i][1];
        a += cross;
        cx += (poly[i][0] + poly[j][0]) * cross;
        cy += (poly[i][1] + poly[j][1]) * cross;
    }
    if (Math.abs(a) < 1e-9) {
        const sx = poly.reduce((s, p) => s + p[0], 0) / poly.length;
        const sy = poly.reduce((s, p) => s + p[1], 0) / poly.length;
        return [sx, sy];
    }
    a *= 0.5;
    return [cx / (6 * a), cy / (6 * a)];
}

/** Offset a closed polygon outward (away from its centroid) by `dist` meters. */
export function offsetPolygonOutward(poly: Polygon2D, dist: number): Polygon2D {
    const [cx, cy] = polygonCentroid(poly);
    return poly.map((p) => {
        const vx = p[0] - cx;
        const vy = p[1] - cy;
        const len = Math.hypot(vx, vy) || 1;
        return [p[0] + (vx / len) * dist, p[1] + (vy / len) * dist] as [number, number];
    });
}

/**
 * Sutherland–Hodgman clip of a polygon against the half-plane defined by the
 * directed line a→b. `keepSide` selects which side survives: 'left' keeps the
 * half-plane to the left of the direction vector, 'right' the opposite.
 */
export function clipPolygonToSide(
    poly: Polygon2D,
    a: [number, number],
    b: [number, number],
    keepSide: 'left' | 'right'
): Polygon2D {
    const side = (p: [number, number]) =>
        (b[0] - a[0]) * (p[1] - a[1]) - (b[1] - a[1]) * (p[0] - a[0]);
    const keepSign = keepSide === 'left' ? 1 : -1;
    const inside = (p: [number, number]) => side(p) * keepSign >= 0;

    const lineIntersect = (p: [number, number], q: [number, number]): [number, number] => {
        const fp = side(p);
        const fq = side(q);
        const t = fp / (fp - fq || 1e-12);
        return [p[0] + t * (q[0] - p[0]), p[1] + t * (q[1] - p[1])];
    };

    const out: Polygon2D = [];
    const n = poly.length;
    for (let i = 0; i < n; i++) {
        const cur = poly[i];
        const prev = poly[(i - 1 + n) % n];
        const curIn = inside(cur);
        const prevIn = inside(prev);
        if (curIn) {
            if (!prevIn) out.push(lineIntersect(prev, cur));
            out.push(cur);
        } else if (prevIn) {
            out.push(lineIntersect(prev, cur));
        }
    }
    return out;
}

/**
 * Split a polygon by the infinite line through `a`→`b`. Returns the two
 * resulting pieces (either may be empty when the line misses the polygon).
 */
export function splitPolygonByLine(
    poly: Polygon2D,
    a: [number, number],
    b: [number, number]
): [Polygon2D, Polygon2D] {
    return [
        clipPolygonToSide(poly, a, b, 'left'),
        clipPolygonToSide(poly, a, b, 'right'),
    ];
}

/** Andrew's monotone-chain convex hull. Returns CCW polygon. */
export function convexHull(points: Polygon2D): Polygon2D {
    const pts = [...points].sort((p, q) => (p[0] - q[0]) || (p[1] - q[1]));
    if (pts.length < 3) return pts;
    const cross = (o: [number, number], a: [number, number], b: [number, number]) =>
        (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
    const lower: Polygon2D = [];
    for (const p of pts) {
        while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0)
            lower.pop();
        lower.push(p);
    }
    const upper: Polygon2D = [];
    for (const p of pts.reverse()) {
        while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0)
            upper.pop();
        upper.push(p);
    }
    lower.pop();
    upper.pop();
    return lower.concat(upper);
}

/** Regular n-gon approximating a cylinder of the given radius at center. */
export function regularPolygon(cx: number, cy: number, radius: number, segments = 24): Polygon2D {
    const poly: Polygon2D = [];
    for (let i = 0; i < segments; i++) {
        const t = (i / segments) * Math.PI * 2;
        poly.push([cx + Math.cos(t) * radius, cy + Math.sin(t) * radius]);
    }
    return poly;
}

/** Axis-aligned rectangle corners CCW from (x0,y0). */
export function rectPolygon(x0: number, y0: number, w: number, h: number): Polygon2D {
    return [
        [x0, y0],
        [x0 + w, y0],
        [x0 + w, y0 + h],
        [x0, y0 + h],
    ];
}
